/**
 * sl-9qm.4.3: LineChart Component
 *
 * SVG-based line chart for time series.
 * X-axis: date column, Y-axis: number column(s).
 */
import { createElement } from 'react';
import { z } from 'zod';

import { VIEW_COMPONENT_TYPES } from '../../../constants/view-component-types';
import type { ViewComponent } from '../../../types/view-component.types';
import * as styles from '../styles.css';

const configSchema = z.object({
  xColumn: z.string().default(''),
  yColumns: z.array(z.string()).default([]),
  showGrid: z.boolean().default(true),
  showTooltip: z.boolean().default(true),
  width: z.number().min(240).max(1200).default(400),
  height: z.number().min(160).max(800).default(250),
  marginTop: z.number().min(0).max(120).default(20),
  marginRight: z.number().min(0).max(120).default(20),
  marginBottom: z.number().min(0).max(160).default(40),
  marginLeft: z.number().min(0).max(160).default(50),
});

const LINE_COLORS = [
  'var(--color-chart-1, var(--streamlain-primary-color))',
  'var(--color-chart-2, #22c7d8)',
  'var(--color-chart-3, #7c5cff)',
  'var(--color-chart-4, #f5b84b)',
  'var(--color-chart-5, #55b86b)',
];

export const LineChartComponent: ViewComponent = {
  id: VIEW_COMPONENT_TYPES.LINE_CHART,
  type: 'data',
  name: 'Line Chart',
  icon: createElement(
    'span',
    { style: { fontSize: 'var(--sl-custom-app-font-size-sm)' } },
    '📈'
  ),
  capabilities: [
    {
      role: 'primary',
      description: 'Time series data rows',
      required: true,
      requirements: [
        { field: 'date', acceptedTypes: ['date'], required: true },
        { field: 'value', acceptedTypes: ['number'], required: true },
      ],
    },
  ],
  configSchema,
  defaultConfig: {
    xColumn: '',
    yColumns: [],
    showGrid: true,
    showTooltip: true,
    width: 400,
    height: 250,
    marginTop: 20,
    marginRight: 20,
    marginBottom: 40,
    marginLeft: 50,
  },
  render({ config, data }) {
    const {
      xColumn,
      yColumns,
      showGrid,
      width,
      height,
      marginTop,
      marginRight,
      marginBottom,
      marginLeft,
    } = config as z.infer<typeof configSchema>;

    if (data.length === 0 || !xColumn || yColumns.length === 0) {
      return createElement(
        'div',
        {
          style: {
            padding: 'var(--sl-custom-app-padding-lg)',
            color: 'var(--streamlain-text-secondary-color)',
            textAlign: 'center' as const,
          },
        },
        'No data — configure xColumn and yColumns'
      );
    }

    // Sort by x-axis
    const sorted = [...data].sort((a, b) => {
      const aDate = new Date(String(a[xColumn] ?? 0)).getTime();
      const bDate = new Date(String(b[xColumn] ?? 0)).getTime();
      return aDate - bDate;
    });

    const margin = {
      top: marginTop,
      right: marginRight,
      bottom: marginBottom,
      left: marginLeft,
    };
    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;

    // Find Y range across all columns
    let yMin = Infinity;
    let yMax = -Infinity;
    for (const row of sorted) {
      for (const col of yColumns) {
        const v = Number(row[col]);
        if (!isNaN(v)) {
          yMin = Math.min(yMin, v);
          yMax = Math.max(yMax, v);
        }
      }
    }
    if (yMin === Infinity) {
      yMin = 0;
      yMax = 100;
    }
    const yRange = yMax - yMin || 1;

    // Build SVG paths for each Y column
    const lines = yColumns.map((col, colIndex) => {
      const points = sorted
        .map((row, i) => {
          const v = Number(row[col]);
          if (isNaN(v)) return null;
          const x = margin.left + (i / Math.max(sorted.length - 1, 1)) * innerW;
          const y = margin.top + innerH - ((v - yMin) / yRange) * innerH;
          return `${x},${y}`;
        })
        .filter(Boolean);

      return createElement('polyline', {
        key: col,
        points: points.join(' '),
        fill: 'none',
        stroke: LINE_COLORS[colIndex % LINE_COLORS.length],
        strokeWidth: 2,
        role: 'graphics-symbol',
        'aria-label': `Line: ${col}`,
      });
    });

    // Grid lines
    const gridLines = showGrid
      ? Array.from({ length: 5 }, (_, i) => {
          const y = margin.top + (i / 4) * innerH;
          return createElement('line', {
            key: `grid-${i}`,
            x1: margin.left,
            y1: y,
            x2: margin.left + innerW,
            y2: y,
            stroke: 'var(--streamlain-border-color)',
            strokeDasharray: '4,4',
          });
        })
      : [];

    return createElement(
      'div',
      {
        role: 'img',
        'aria-label': `Line chart: ${yColumns.join(', ')} over ${xColumn}`,
      },
      createElement(
        'svg',
        {
          width,
          height,
          viewBox: `0 0 ${width} ${height}`,
        },
        ...gridLines,
        ...lines
      )
    );
  },
  palettePreview() {
    return createElement(
      'div',
      { className: styles.palettePreview },
      '📈 Line Chart'
    );
  },
};
